import * as THREE from 'three';
import { SceneManager } from './SceneManager.js';
import { DeviceManager } from './DeviceManager.js';
import { LabelManager } from './LabelManager.js';
import { MockDataGenerator } from './MockDataGenerator.js';
import './dashboard-wiring.js';

const canvas = document.querySelector('.webgl');

// Loading Screen Elements
const loadingOverlay = document.getElementById('loading-overlay');
const loadingProgress = document.getElementById('loading-progress');
const loadingPercentage = document.getElementById('loading-percentage');

// Loading State
let simulatedProgress = 0;
let isLoadingComplete = false;
let animationFrameId = null;

// Simulate smooth progress 0→80% over 2 seconds
function startSimulatedLoading() {
    const duration = 2000; // 2 seconds
    const startTime = performance.now();
    const targetProgress = 80;
    
    function updateSimulatedProgress(currentTime) {
        const elapsed = currentTime - startTime;
        const rawProgress = Math.min(elapsed / duration, 1);
        
        // Ease out cubic for smooth deceleration
        const easedProgress = 1 - Math.pow(1 - rawProgress, 3);
        simulatedProgress = easedProgress * targetProgress;
        
        updateLoadingUI(simulatedProgress);
        
        if (rawProgress < 1 && !isLoadingComplete) {
            animationFrameId = requestAnimationFrame(updateSimulatedProgress);
        }
    }
    
    animationFrameId = requestAnimationFrame(updateSimulatedProgress);
}

// Update loading screen visuals
function updateLoadingUI(progress) {
    const percent = Math.round(progress);
    if (loadingProgress) {
        loadingProgress.style.width = `${percent}%`;
    }
    if (loadingPercentage) {
        loadingPercentage.textContent = `${percent}%`;
    }
}

// Complete loading and fade out
function completeLoading() {
    isLoadingComplete = true;
    
    // Cancel any running simulation
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
    }
    
    // Jump to 100%
    updateLoadingUI(100);
    
    // Small delay so user sees 100%
    setTimeout(() => {
        if (loadingOverlay) {
            loadingOverlay.classList.add('fade-out');
            
            // Remove from DOM after fade
            setTimeout(() => {
                if (loadingOverlay && loadingOverlay.parentNode) {
                    loadingOverlay.remove();
                }
            }, 800); // Match CSS transition
        }
    }, 300);
}

// Initialize systems
const sceneManager = new SceneManager(canvas);
const deviceManager = new DeviceManager(sceneManager.scene);
const labelManager = new LabelManager(sceneManager, deviceManager);

// Expose for console testing
window.sceneManager = sceneManager;
window.deviceManager = deviceManager;
window.labelManager = labelManager;

// Load everything
async function init() {
    // Start simulated progress immediately
    startSimulatedLoading();
    
    try {
        // Load architecture and get slots
        await sceneManager.loadArchitecture('./architecture.glb');
        const slotPositions = sceneManager.getAllSlotPositions();
        
        // Load detectors
        await deviceManager.loadPrototypes();
        const manifest = await deviceManager.loadManifest('./detectors.json');
        deviceManager.populate(manifest, slotPositions);
        
        // Initialize Mock Data Generator after devices are loaded
        const mockData = new MockDataGenerator(deviceManager);
        window.mockData = mockData;
        
        // Loading complete - trigger fade out
        completeLoading();
        
    } catch (error) {
        console.error('Loading failed:', error);
        // Still complete loading to avoid stuck screen
        completeLoading();
    }
}

// Start loading
init();

// Animation loop
const clock = new THREE.Clock();

function tick() {
    const deltaTime = clock.getDelta();
    const currentTime = performance.now();
    
    sceneManager.update();
    deviceManager.update(deltaTime);
    labelManager.update();
    
    // Update mock data if initialized
    if (window.mockData) {
        window.mockData.update(deltaTime, currentTime);
    }
    
    requestAnimationFrame(tick);
}

// Start animation loop immediately (renders behind loading screen)
tick();